// ABOUTME: Thread card for a request an outside agent platform sent the Marketing
// lead: who asked ("via <platform>"), what, where it stands, and what the owner
// can do — approve what is waiting, change it (starts a message about it) or
// cancel it. Its state is read live and refreshed by the event stream.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, CircleAlert, Clock, Loader2, MessageSquarePlus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cancelPlatformRequest, getPlatformRequest } from '@/api/platformRequests'
import type { PlatformRequest, PlatformRequestStatus } from '@/api/platformRequests'
import { useApprovalActions } from '@/components/chat/useChatApprovals'
import { useUiStore } from '@/stores/useUiStore'
import { cn } from '@/lib/utils'
import { CHAT_CARD_BLOCK } from './chatCard'
import { composeAboutRequest } from './platformRequestEvents'

const STATUS_COPY: Record<PlatformRequestStatus, string> = {
  received: 'Received',
  in_progress: 'Working on it',
  awaiting_owner_approval: 'Waiting for your approval',
  completed: 'Done',
  failed: "Couldn't be done",
  cancelled: 'Cancelled',
}

function StatusIcon({ status }: { status: PlatformRequestStatus }) {
  if (status === 'completed') return <Check className="text-success size-4" aria-hidden />
  if (status === 'failed' || status === 'cancelled')
    return <CircleAlert className="text-destructive size-4" aria-hidden />
  if (status === 'awaiting_owner_approval')
    return <Clock className="text-warning size-4" aria-hidden />
  return <Loader2 className="text-muted-foreground size-4 animate-spin" aria-hidden />
}

function Outcome({ request }: { request: PlatformRequest }) {
  if (request.status === 'completed' && request.summary) {
    return (
      <div className="space-y-1 text-sm">
        <p>{request.summary}</p>
        {[...request.preview_urls, ...request.report_urls].map((url) => (
          <a
            key={url}
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-primary block truncate underline underline-offset-2"
          >
            {url}
          </a>
        ))}
      </div>
    )
  }
  if (request.error && request.status !== 'completed') {
    return <p className="text-muted-foreground text-sm">{request.error}</p>
  }
  return null
}

export function PlatformRequestCard({
  requestId,
  platform,
}: {
  requestId: string
  platform: string
}) {
  const queryClient = useQueryClient()
  const { approve } = useApprovalActions()
  const showToast = useUiStore((s) => s.showToast)
  const [approving, setApproving] = useState(false)
  const { data: request } = useQuery({
    queryKey: ['marketing-task', requestId],
    queryFn: () => getPlatformRequest(requestId),
  })
  const cancel = useMutation({
    mutationFn: () => cancelPlatformRequest(requestId),
    onSuccess: (updated) => queryClient.setQueryData(['marketing-task', requestId], updated),
  })

  const status = request?.status
  const open =
    status === 'received' || status === 'in_progress' || status === 'awaiting_owner_approval'

  return (
    <div
      className={cn(CHAT_CARD_BLOCK)}
      data-testid="platform-request-card"
      data-task-id={requestId}
      data-status={status}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs font-medium">via {platform}</span>
        {status ? (
          <span className="flex items-center gap-1.5 text-xs font-medium">
            <StatusIcon status={status} />
            {STATUS_COPY[status]}
          </span>
        ) : null}
      </div>
      <p className="line-clamp-4 text-sm break-words">{request?.brief ?? 'Loading request…'}</p>
      {request?.publish_requested && open ? (
        <p className="text-muted-foreground text-xs">
          They asked for this to be published. Nothing goes out until you approve it.
        </p>
      ) : null}
      {request ? <Outcome request={request} /> : null}

      {open && request ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {status === 'awaiting_owner_approval' && request.approval_id ? (
            <Button
              className="h-11 flex-1"
              disabled={approving}
              onClick={async () => {
                setApproving(true)
                try {
                  await approve(request.approval_id as string)
                } catch (err) {
                  showToast({ type: 'error', title: (err as Error).message })
                }
                await queryClient.invalidateQueries({ queryKey: ['marketing-task', requestId] })
                setApproving(false)
              }}
            >
              {approving ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Approve
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="h-11 flex-1"
            onClick={() =>
              composeAboutRequest(`About ${platform}'s request ("${request.brief.slice(0, 60)}"): `)
            }
          >
            <MessageSquarePlus className="mr-2 size-4" />
            Change
          </Button>
          <Button
            variant="ghost"
            className="h-11 flex-1"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            <X className="mr-2 size-4" />
            Cancel
          </Button>
        </div>
      ) : null}
    </div>
  )
}
